import { Tooltip } from '../components/Tooltip';
import type { ClassGrade, DmScanMeasurements, DmSidedClass } from './types';

interface IndicesPanelProps {
    measurements: DmScanMeasurements | null;
}

const EMPTY = '—';

const GRADE_STYLE: Record<ClassGrade, string> = {
    I: 'bg-emerald-100 text-emerald-800',
    II: 'bg-amber-100 text-amber-800',
    'II.2': 'bg-amber-100 text-amber-800',
    III: 'bg-red-100 text-red-800',
    '?': 'bg-gray-100 text-gray-400'
};

interface ArchMetricProps {
    label: string;
    fullLabel: string;
    value: string | null;
}

function ArchMetric({ label, fullLabel, value }: ArchMetricProps) {
    return (
        <Tooltip content={fullLabel}>
            <div className="flex items-baseline justify-center gap-1 text-xs">
                <span className="text-[10px] uppercase tracking-wider text-gray-400">{label}</span>
                <span className="text-gray-800 font-medium tabular-nums">{value ?? EMPTY}</span>
            </div>
        </Tooltip>
    );
}

function ClassCell({ data }: { data: DmSidedClass }) {
    // Grid with fixed-size columns + mx-auto so the chip sits at the same
    // horizontal offset across every row regardless of distance text width.
    return (
        <div className="mx-auto grid grid-cols-[auto_2.5rem] items-center gap-1.5 text-xs">
            <Tooltip content={data.grade === '?' ? 'Class unknown' : `Class ${data.grade}`}>
                <span
                    className={`justify-self-end inline-flex items-center justify-center min-w-[1.5rem] h-5 px-1 rounded text-[11px] font-semibold tabular-nums ${GRADE_STYLE[data.grade] ?? GRADE_STYLE['?']}`}
                >
                    {data.grade}
                </span>
            </Tooltip>
            <span className="justify-self-start text-gray-500 tabular-nums">
                {data.distance ?? EMPTY}
            </span>
        </div>
    );
}

function IndicesPanel({ measurements }: IndicesPanelProps) {
    return (
        <div className="rounded-xl border border-gray-200 bg-white p-3 flex-shrink-0">
            <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">
                Indices
            </h4>

            {!measurements ? (
                <div className="text-xs text-gray-400 italic">No measurements yet</div>
            ) : (
                <>
                    <div className="grid grid-cols-3 gap-2 mb-3">
                        <ArchMetric label="OB" fullLabel="Overbite" value={measurements.overbite} />
                        <ArchMetric label="OJ" fullLabel="Overjet" value={measurements.overjet} />
                        <ArchMetric
                            label="MD"
                            fullLabel="Midline deviation"
                            value={measurements.midline}
                        />
                    </div>

                    <div className="pt-2 border-t border-gray-100">
                        <div className="grid grid-cols-[auto_1fr_1fr] gap-x-3 gap-y-1.5 items-center">
                            <span />
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 text-center">
                                R
                            </span>
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 text-center">
                                L
                            </span>

                            <span className="text-xs text-gray-500">Molar</span>
                            <ClassCell data={measurements.molarRight} />
                            <ClassCell data={measurements.molarLeft} />

                            <span className="text-xs text-gray-500">Cuspid</span>
                            <ClassCell data={measurements.cuspidRight} />
                            <ClassCell data={measurements.cuspidLeft} />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

export { IndicesPanel };
